/**
 * Typed REST + WebSocket client for the Raydn reviewer UI.
 *
 * Design goals
 * ------------
 * - Single, flat `api` namespace object — discoverable in IDE autocomplete.
 * - Each function's return type is a concrete interface (`UploadResponse`,
 *   `FiaArtifact`, etc.) so calling code never has to cast.
 * - Non-2xx responses throw `ApiError` — callers handle with try/catch.
 * - `wakeUp()` lets pages show a cold-start banner during Render's first-
 *   request spin-up without coupling that UX to individual API functions.
 */

import {
  AnomaliesResponse,
  AnomalyAckResponse,
  ApproveResponse,
  ApiError,
  AuditEntry,
  CleanedPreviewResponse,
  CreateRunRequest,
  CreateRunResponse,
  CrossFieldFlagsResponse,
  DiffResponse,
  DownloadUrlResponse,
  FiaArtifact,
  HitlActionRequest,
  HitlActionResponse,
  HitlBatchConfirmRequest,
  HitlBatchConfirmResponse,
  HitlItem,
  HitlListQuery,
  Manifest,
  PiiRevealResponse,
  PiiRevealSurface,
  ProvenanceResponse,
  RasSchemaResponse,
  RerunRequest,
  RerunResponse,
  ReviewerDefinedRuleRequest,
  ReviewerDefinedRuleResponse,
  RunAuditEvent,
  RunEvent,
  RunSummary,
  UpdateColumnPoliciesRequest,
  UpdateColumnPoliciesResponse,
  UploadResponse,
} from './types';

// ----------------------------------------------------------------------------
// Config
// ----------------------------------------------------------------------------

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const WS_BASE_URL = import.meta.env.VITE_WS_BASE_URL ?? '/ws';

// ----------------------------------------------------------------------------
// Low-level request helper
// ----------------------------------------------------------------------------

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** If true, body is sent as-is (e.g. FormData); otherwise JSON-stringified. */
  raw?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, raw = false } = opts;

  const init: RequestInit = { method, signal };
  if (body !== undefined) {
    if (raw) {
      init.body = body as BodyInit;
    } else {
      init.body = JSON.stringify(body);
      init.headers = { 'Content-Type': 'application/json' };
    }
  }

  const res = await fetch(`${BASE_URL}${path}`, init);

  // Try to parse JSON for both success and failure paths — the server
  // usually returns structured error envelopes.
  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const parsed: unknown = isJson ? await res.json().catch(() => undefined) : undefined;

  if (!res.ok) {
    // Extract a human-readable message from the common error envelopes:
    //   {"message": "..."}                — our own structured errors
    //   {"detail": {"message": "..."}}    — our HTTPException(detail={...})
    //   {"detail": "..."}                 — FastAPI default HTTPException
    // Fall back to res.statusText, which is empty over HTTP/2 (Render serves
    // through Cloudflare on h2), so the final fallback is `HTTP <code>`.
    const extract = (value: unknown): string | undefined => {
      if (value && typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        if (typeof obj.message === 'string') return obj.message;
        if (typeof obj.detail === 'string') return obj.detail;
        if (obj.detail && typeof obj.detail === 'object') {
          const detail = obj.detail as Record<string, unknown>;
          if (typeof detail.message === 'string') return detail.message;
        }
      }
      return undefined;
    };
    const message = extract(parsed) || res.statusText || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

// ----------------------------------------------------------------------------
// Cold-start wake-up
// ----------------------------------------------------------------------------

/**
 * Ping `/health` and resolve when the API returns 200.
 *
 * Designed for Render's free tier where the web service spins down after
 * inactivity and takes 30–60s to cold-start. Caller can show a banner while
 * this resolves.
 *
 * @param onAttempt   Callback invoked on each attempt (useful for UX text).
 * @param maxAttempts Hard cap — default 40 × 2s = 80 s window.
 */
export async function wakeUp(
  onAttempt?: (attempt: number) => void,
  maxAttempts = 40,
): Promise<{ awake: true; attempts: number }> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    onAttempt?.(attempt);
    try {
      const res = await fetch(`${BASE_URL}/health`, { method: 'GET' });
      if (res.ok) return { awake: true, attempts: attempt };
    } catch {
      // network error — keep retrying until the window closes
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new ApiError(504, `API did not respond after ${maxAttempts} attempts`);
}

// ----------------------------------------------------------------------------
// REST endpoints
// ----------------------------------------------------------------------------

export const api = {
  // --- Files + runs ---------------------------------------------------------

  uploadFile(file: File, signal?: AbortSignal): Promise<UploadResponse> {
    const form = new FormData();
    form.append('file', file);
    return request<UploadResponse>('/upload', {
      method: 'POST',
      body: form,
      signal,
      raw: true,
    });
  },

  createRun(body: CreateRunRequest): Promise<CreateRunResponse> {
    return request<CreateRunResponse>('/runs', { method: 'POST', body });
  },

  getRun(runId: string): Promise<RunSummary> {
    return request<RunSummary>(`/runs/${encodeURIComponent(runId)}`);
  },

  // --- Artifacts ------------------------------------------------------------

  getFiaArtifact(runId: string): Promise<FiaArtifact> {
    return request<FiaArtifact>(`/runs/${encodeURIComponent(runId)}/artifact?type=fia`);
  },

  getSchema(runId: string): Promise<RasSchemaResponse> {
    return request<RasSchemaResponse>(`/runs/${encodeURIComponent(runId)}/schema`);
  },

  getManifest(runId: string): Promise<Manifest> {
    return request<Manifest>(`/runs/${encodeURIComponent(runId)}/manifest`);
  },

  saveManifest(runId: string, manifest: Manifest): Promise<Manifest> {
    return request<Manifest>(`/runs/${encodeURIComponent(runId)}/manifest`, {
      method: 'POST',
      body: manifest,
    });
  },

  // --- HITL -----------------------------------------------------------------

  listHits(runId: string): Promise<HitlItem[]> {
    // v0.6 Stage 1 (H2) — explicit `status=open` filter, defence-in-depth
    // for the H1 backend default. The reviewer queue only ever wants open
    // hits; resolved/escalated audit views go through `queryHits` with an
    // explicit status filter.
    return request<HitlItem[]>(
      `/hits?run_id=${encodeURIComponent(runId)}&status=open`,
    );
  },

  /**
   * Cross-run HITL query. All filter fields optional; empty query returns
   * every open/resolved/escalated hit in the system (mock caps at 1k).
   */
  queryHits(filters: HitlListQuery = {}): Promise<HitlItem[]> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
    const tail = qs.toString();
    return request<HitlItem[]>(`/hits${tail ? `?${tail}` : ''}`);
  },

  getAuditTrail(hitId: string): Promise<AuditEntry[]> {
    return request<AuditEntry[]>(`/hits/${encodeURIComponent(hitId)}/audit`);
  },

  postHitAction(hitId: string, action: HitlActionRequest): Promise<HitlActionResponse> {
    return request<HitlActionResponse>(`/hits/${encodeURIComponent(hitId)}/action`, {
      method: 'POST',
      body: action,
    });
  },

  /**
   * v0.5 Stage D — bulk-confirm a set of recommended-tier hits in
   * one round-trip. The backend writes per-hit audit events (so
   * carry-forward queries are unaffected) plus a single run-scoped
   * summary event recording the batch.
   */
  postBatchConfirm(
    runId: string,
    body: HitlBatchConfirmRequest,
  ): Promise<HitlBatchConfirmResponse> {
    return request<HitlBatchConfirmResponse>(
      `/runs/${encodeURIComponent(runId)}/hits/batch_confirm`,
      { method: 'POST', body },
    );
  },

  // --- Stage 2: Output surface ----------------------------------------------

  getCleaned(
    runId: string,
    params: { table_id?: string; limit?: number; offset?: number } = {},
  ): Promise<CleanedPreviewResponse> {
    const qs = new URLSearchParams();
    if (params.table_id) qs.set('table_id', params.table_id);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const tail = qs.toString();
    return request<CleanedPreviewResponse>(
      `/runs/${encodeURIComponent(runId)}/cleaned${tail ? `?${tail}` : ''}`,
    );
  },

  getDiff(
    runId: string,
    params: { table_id?: string; column?: string; limit?: number; offset?: number } = {},
  ): Promise<DiffResponse> {
    const qs = new URLSearchParams();
    if (params.table_id) qs.set('table_id', params.table_id);
    if (params.column) qs.set('column', params.column);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const tail = qs.toString();
    return request<DiffResponse>(
      `/runs/${encodeURIComponent(runId)}/diff${tail ? `?${tail}` : ''}`,
    );
  },

  getProvenance(runId: string, tableId?: string): Promise<ProvenanceResponse> {
    const qs = tableId ? `?table_id=${encodeURIComponent(tableId)}` : '';
    return request<ProvenanceResponse>(
      `/runs/${encodeURIComponent(runId)}/provenance${qs}`,
    );
  },

  getAnomalies(runId: string, tableId?: string): Promise<AnomaliesResponse> {
    const qs = tableId ? `?table_id=${encodeURIComponent(tableId)}` : '';
    return request<AnomaliesResponse>(
      `/runs/${encodeURIComponent(runId)}/anomalies${qs}`,
    );
  },

  /**
   * v0.4 Stage D.4 — paginated cross-field violation flags.
   *
   * The backend caps the underlying list at 1000 entries per rule
   * (with a per-rule truncation summary row), so pagination here is a
   * UX concern. ``limit`` is bounded server-side at 500.
   */
  getCrossFieldFlags(
    runId: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<CrossFieldFlagsResponse> {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const tail = qs.toString();
    return request<CrossFieldFlagsResponse>(
      `/runs/${encodeURIComponent(runId)}/cross_field_flags${tail ? `?${tail}` : ''}`,
    );
  },

  /**
   * v0.5 Stage E.3 — author a reviewer-defined cross-field rule.
   *
   * The backend writes a rule-scoped audit event with
   * ``event_type="reviewer_defined"`` (carry-forward keys on
   * ``file_hash + rule_hash`` so a same-bytes re-upload picks the
   * rule up automatically) and re-kicks the pipeline. The CPA
   * detection step merges the reviewer-defined rule with any
   * heuristic candidates and treats it as auto-confirmed (no
   * confirm-rule HITL surfaced) — see DECISIONS Stage E.3.
   */
  createReviewerDefinedRule(
    runId: string,
    body: ReviewerDefinedRuleRequest,
  ): Promise<ReviewerDefinedRuleResponse> {
    return request<ReviewerDefinedRuleResponse>(
      `/runs/${encodeURIComponent(runId)}/rules`,
      { method: 'POST', body },
    );
  },

  /**
   * v0.5 Stage E.4 — persist reviewer-set per-column policy
   * overrides + re-kick the run.
   *
   * The backend writes one column-scoped audit event per entry in
   * ``body.overrides`` (``target_type="column_policy"``,
   * ``target_id=<column>``, ``event_type="invalid_threshold_set"``
   * or ``"invalid_threshold_cleared"``) and merges the latest
   * overrides into ``cpa_config["column_policies"]`` before
   * invoking CPA. Carry-forward keys on ``file_hash`` so a same-
   * bytes re-upload picks the override up automatically.
   */
  updateColumnPolicies(
    runId: string,
    body: UpdateColumnPoliciesRequest,
  ): Promise<UpdateColumnPoliciesResponse> {
    return request<UpdateColumnPoliciesResponse>(
      `/runs/${encodeURIComponent(runId)}/column_policies`,
      { method: 'PATCH', body },
    );
  },

  approveOutput(runId: string): Promise<ApproveResponse> {
    return request<ApproveResponse>(`/runs/${encodeURIComponent(runId)}/approve`, {
      method: 'POST',
    });
  },

  requestDownload(runId: string): Promise<DownloadUrlResponse> {
    return request<DownloadUrlResponse>(
      `/runs/${encodeURIComponent(runId)}/audit/download`,
      { method: 'POST' },
    );
  },

  acknowledgeAnomaly(runId: string, anomalyId: string): Promise<AnomalyAckResponse> {
    return request<AnomalyAckResponse>(
      `/runs/${encodeURIComponent(runId)}/anomalies/${encodeURIComponent(anomalyId)}/ack`,
      { method: 'POST' },
    );
  },

  /** v0.6 Stage A.3 — record a PII-reveal action against the run audit
   * log. Called from the TablePreview ``onRequestReveal`` callback at
   * Output-tab and Schema/FIA-tab call sites. The reveal is gated on a
   * successful POST: if this rejects, the cells stay blurred and the
   * caller surfaces the failure. ``surface`` distinguishes the two
   * preview tabs in the audit timeline. */
  recordPiiReveal(
    runId: string,
    surface: PiiRevealSurface,
    piiColumns: string[],
  ): Promise<PiiRevealResponse> {
    return request<PiiRevealResponse>(
      `/runs/${encodeURIComponent(runId)}/pii_reveal`,
      {
        method: 'POST',
        body: { surface, pii_columns: piiColumns },
      },
    );
  },

  getRunAudit(runId: string, action?: string): Promise<RunAuditEvent[]> {
    const qs = action ? `?action=${encodeURIComponent(action)}` : '';
    return request<RunAuditEvent[]>(
      `/runs/${encodeURIComponent(runId)}/audit${qs}`,
    );
  },

  // --- Re-run controls ------------------------------------------------------

  rerun(runId: string, body: RerunRequest): Promise<RerunResponse> {
    return request<RerunResponse>(`/runs/${encodeURIComponent(runId)}/rerun`, {
      method: 'POST',
      body,
    });
  },

  // --- Carry-forward decision wipe (v0.6 Stage B7) --------------------------

  /**
   * Number of carry-forward HITL decision rows currently in
   * ``audit_events``. The upload page reads this on mount and hides
   * the "Clear memory" button when the count is zero (nothing to
   * clear).
   */
  getDecisionCount(): Promise<{ count: number }> {
    return request<{ count: number }>('/store/decisions/count');
  },

  /**
   * Wipe every carry-forward HITL decision (DELETE on the audit-events
   * backing store). Returns ``{cleared: true, rows_deleted}``.
   * Idempotent — re-clearing a clean store still returns
   * ``cleared: true``.
   */
  clearDecisions(): Promise<{ cleared: boolean; rows_deleted: number }> {
    return request<{ cleared: boolean; rows_deleted: number }>(
      '/store/decisions',
      { method: 'DELETE' },
    );
  },
};

// ----------------------------------------------------------------------------
// WebSocket client
// ----------------------------------------------------------------------------

export interface RunEventsHandle {
  close: () => void;
  /** Returns the current ready state. Useful for test assertions. */
  readyState: () => number;
}

/**
 * Open a WebSocket to stream run events. Handler is invoked for each parsed
 * `RunEvent`; parse failures are logged and swallowed.
 *
 * The caller is responsible for closing the handle when the component
 * unmounts; see `useRunEvents` (Stage 2) for a React hook that does that
 * automatically.
 */
export function connectRunEvents(
  runId: string,
  onEvent: (e: RunEvent) => void,
  onError?: (err: Event) => void,
): RunEventsHandle {
  // Absolute ws:// / wss:// URLs pass through; relative ones are resolved
  // against window.location (dev server's Vite proxy, or the current host
  // in production).
  const url = WS_BASE_URL.startsWith('ws')
    ? `${WS_BASE_URL}/runs/${encodeURIComponent(runId)}/events`
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${WS_BASE_URL}/runs/${encodeURIComponent(runId)}/events`;

  const ws = new WebSocket(url);

  ws.addEventListener('message', (msg) => {
    try {
      onEvent(JSON.parse(msg.data) as RunEvent);
    } catch (err) {
      console.warn('[runEvents] failed to parse message', err, msg.data);
    }
  });

  if (onError) ws.addEventListener('error', onError);

  return {
    close: () => ws.close(),
    readyState: () => ws.readyState,
  };
}

// Re-export types for ergonomic single-import consumption.
export * from './types';
